import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

export const eligibleSupervisorDtoSchema = z.object({
  id: z.string(),
  nameEn: z.string(),
  nameAr: z.string(),
  username: z.string(),
});
export type EligibleSupervisorDto = z.infer<typeof eligibleSupervisorDtoSchema>;

export const eligibleSupervisorsResponseDtoSchema = z.object({
  data: z.array(eligibleSupervisorDtoSchema),
});

const branchRefDtoSchema = z.object({
  id: z.string(),
  nameEn: z.string(),
  nameAr: z.string(),
});

const createdAssetDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  nameEn: z.string(),
  nameAr: z.string(),
  branch: branchRefDtoSchema,
  accountId: z.string(),
});

const createdAssignmentDtoSchema = z.object({
  id: z.string(),
  supervisorId: z.string(),
  assetId: z.string(),
  assignedAt: z.string(),
  endedAt: z.string().nullable(),
  supervisor: eligibleSupervisorDtoSchema,
  asset: createdAssetDtoSchema,
});
export type CreatedAssignmentDto = z.infer<typeof createdAssignmentDtoSchema>;

export const createSupervisorAssignmentResponseDtoSchema = z.object({
  data: z.array(createdAssignmentDtoSchema),
});

export const supervisorAssignmentCreateInputDtoSchema = z.object({
  supervisorId: z.string(),
  assetIds: z.array(z.string()),
});

// Shared shape: an asset with its code, bilingual names and branch ref --
// identical between the detail endpoint's per-assignment asset and
// GET /supervisor-assignments/assets' own rows.
const assetWithBranchDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  nameEn: z.string(),
  nameAr: z.string(),
  branch: branchRefDtoSchema,
});
export type AssetWithBranchDto = z.infer<typeof assetWithBranchDtoSchema>;

export const assignableAssetsResponseDtoSchema = z.object({
  data: z.array(assetWithBranchDtoSchema),
});

const detailAssignmentDtoSchema = z.object({
  id: z.string(),
  asset: assetWithBranchDtoSchema,
  assignedAt: z.string(),
});

const supervisorAssignmentDetailDtoSchema = z.object({
  supervisor: eligibleSupervisorDtoSchema,
  assignments: z.array(detailAssignmentDtoSchema),
});
export type SupervisorAssignmentDetailDto = z.infer<
  typeof supervisorAssignmentDetailDtoSchema
>;

export const supervisorAssignmentDetailResponseDtoSchema = z.object({
  data: supervisorAssignmentDetailDtoSchema,
});

// GET /supervisor-assignments -- one row per supervisor, keyed by
// supervisorId (there's no separate "assignment row" id at this rollup
// level, only per supervisor-asset pair, same as the detail endpoint).
export const supervisorAssignmentListItemDtoSchema = z.object({
  supervisorId: z.string(),
  supervisorNameEn: z.string(),
  supervisorNameAr: z.string(),
  username: z.string(),
  assetCount: z.number(),
  lastUpdatedAt: z.string(),
});
export type SupervisorAssignmentListItemDto = z.infer<
  typeof supervisorAssignmentListItemDtoSchema
>;

export const supervisorAssignmentListResponseDtoSchema = z.object({
  data: z.array(supervisorAssignmentListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

// PATCH /supervisor-assignments -- same request/response shape as create.
export const supervisorAssignmentUpdateInputDtoSchema =
  supervisorAssignmentCreateInputDtoSchema;

// DELETE /supervisor-assignments/supervisors/:supervisorId
export const deleteSupervisorAssignmentResponseDtoSchema = z.object({
  data: z.object({ removed: z.number() }),
  message: z.string(),
});

// GET /supervisor-assignments/history -- read-only audit rows.
const supervisorAssignmentHistoryActionDtoSchema = z.enum([
  "Created",
  "Updated",
  "Removed",
]);

export const supervisorAssignmentHistoryItemDtoSchema = z.object({
  id: z.string(),
  assignmentId: z.string(),
  accountId: z.string(),
  supervisorId: z.string(),
  supervisorNameEn: z.string(),
  supervisorNameAr: z.string(),
  assetId: z.string(),
  assetNameEn: z.string(),
  assetNameAr: z.string(),
  assignedById: z.string(),
  assignedByName: z.string(),
  actionById: z.string(),
  action: supervisorAssignmentHistoryActionDtoSchema,
  status: z.string(),
  assignedAt: z.string(),
  actionAt: z.string(),
});
export type SupervisorAssignmentHistoryItemDto = z.infer<
  typeof supervisorAssignmentHistoryItemDtoSchema
>;

export const supervisorAssignmentHistoryResponseDtoSchema = z.object({
  data: z.array(supervisorAssignmentHistoryItemDtoSchema),
  meta: paginationMetaDtoSchema,
});
